import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  EXAMPLES,
  FAMILIES,
  GATEWAY,
  GRPC,
  HTTP_STATUSES,
  METHODS,
  NF_CODES,
  PLAN_ROWS,
  UPDATED,
  WEBSOCKET,
  buildCurl,
  getNetwork,
  groupOfNetwork,
  networksByFamily,
} from "../../data/apiReference";
import { SUI_BLOCKS, SUI_META } from "../../data/suiReference";
import { NODEGATE_API, NODEGATE_STACK_SERVICES, providersForNetwork, serviceById } from "../../data/services";
import { NETWORKS_PATH, networkPath, useNetworkRoute } from "../../lib/networkRoute";
import { Block, Callout, CodeBlock, CopyButton, DocTable, KVCard, Mono, SectionHeading, scrollToId } from "../../Components/api/DocBlocks";
import ChainLogo from "../../Components/api/ChainLogo";
import { implLabel, implSections } from "../../Components/api/implMeta";

// Right-hand "On this page" list with scroll-spy (xl screens).
const OnThisPage = ({ sections, accent }) => {
  const [active, setActive] = useState(sections[0]?.id);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-20% 0px -70% 0px" },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);
  if (!sections.length) return null;
  return (
    <nav className="sticky top-28 hidden w-48 shrink-0 xl:block" aria-label="On this page">
      <p className="m-0 mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">On this page</p>
      <ul className="m-0 list-none space-y-1 border-l border-white/10 p-0">
        {sections.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => scrollToId(s.id)}
              className={`-ml-px block w-full border-0 border-l-2 bg-transparent py-1 text-left text-xs transition-colors ${s.sub ? "pl-6" : "pl-3"}`}
              style={active === s.id ? { color: accent, borderColor: accent } : { color: "#9ca3af", borderColor: "transparent" }}
            >
              {s.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const Badge = ({ children, accent, className = "" }) => (
  <span
    className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${className}`}
    style={accent ? { color: accent, borderColor: `${accent}66`, backgroundColor: `${accent}14` } : { borderColor: "rgba(255,255,255,0.12)", color: "#d1d5db" }}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Header — the network (ecosystem + chain id), not a single slug.
// ---------------------------------------------------------------------------
const NetworkHeader = ({ network, group, providers, fam }) => {
  const parts = [`${network.chains.length} Datanodes ${network.chains.length === 1 ? "interface" : "interfaces"}`];
  if (providers.nodegate) parts.push("Nodegate stack");
  if (providers.metrics) parts.push("Chain Graph metrics");
  const tags = ["Datanodes", ...network.chains.map(implLabel), ...(providers.nodegate ? ["Nodegate"] : []), ...(providers.metrics ? ["Chain Graph"] : [])];
  return (
    <header className="doc-reveal relative mb-10 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
      <span className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: fam.accent }} />
      <div className="relative flex flex-wrap items-center gap-4">
        <ChainLogo chain={{ name: network.name, logo: network.logo, family: network.family }} size={56} />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">{group.name}</p>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 text-3xl font-bold text-white md:text-4xl">{network.fullName}</h1>
            <Badge accent={network.env === "mainnet" ? fam.accent : undefined}>{network.env}</Badge>
          </div>
          <p className="m-0 mt-1 text-sm text-gray-400">On the Nodefleet fleet as {parts.join(" · ")}.</p>
        </div>
        <a
          href={GATEWAY.dashboard}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-space-deep no-underline shadow-glow-sm transition-colors hover:bg-primary-400"
        >
          Get an API key
        </a>
      </div>
      <div className="relative mt-5 flex flex-wrap items-center gap-2">
        {tags.map((t) => (
          <span key={t} className="rounded-md px-2 py-1 text-xs font-semibold" style={{ color: fam.accent, backgroundColor: `${fam.accent}1A` }}>
            {t}
          </span>
        ))}
        {network.chainId !== undefined && (
          <span className="ml-auto flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0A14] py-1 pl-3 pr-1 font-mono text-xs text-gray-300">
            chain id: <span className="text-white">{String(network.chainId)}</span>
            <CopyButton text={String(network.chainId)} label="Copy chain id" />
          </span>
        )}
      </div>
    </header>
  );
};

// ---------------------------------------------------------------------------
// "API reference available" — one card per Nodefleet service covering the network.
// ---------------------------------------------------------------------------
const ProviderCard = ({ service, accent, children, anchor, external }) => (
  <div className="doc-reveal flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5" style={{ "--accent": accent }}>
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
        {service.logo ? (
          <img src={service.logo} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
        ) : (
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 17l5-6 4 4 6-8 3 3" />
            <path d="M3 21h18" />
          </svg>
        )}
      </span>
      <div className="min-w-0">
        <p className="m-0 text-sm font-bold text-white">{service.name}</p>
        <p className="m-0 text-xs text-gray-500">{service.tagline}</p>
      </div>
    </div>
    <div className="mt-4 flex-1 text-xs text-gray-300">{children}</div>
    <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/10 pt-3 text-xs">
      <button type="button" onClick={() => scrollToId(anchor)} className="border-0 bg-transparent p-0 font-semibold no-underline hover:underline" style={{ color: accent }}>
        Read the section ↓
      </button>
      {external && (
        <a href={external.href} target="_blank" rel="noopener noreferrer" className="text-gray-400 no-underline hover:text-white">
          {external.label} ↗
        </a>
      )}
    </div>
  </div>
);

const ProvidersGrid = ({ network, providers, impl, setImpl }) => {
  const datanodes = serviceById("datanodes");
  const nodegate = serviceById("nodegate");
  const chaingraph = serviceById("chaingraph");
  return (
    <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <ProviderCard service={datanodes} accent={datanodes.accent} anchor="datanodes" external={{ href: GATEWAY.dashboard, label: "Dashboard" }}>
        <p className="m-0 mb-2 text-gray-400">
          {network.chains.length === 1 ? "One interface" : `${network.chains.length} interfaces`} behind <Mono>{GATEWAY.rpc.replace("https://", "")}</Mono>:
        </p>
        <ul className="m-0 list-none space-y-1 p-0">
          {network.chains.map((c) => (
            <li key={c.slug}>
              <button
                type="button"
                onClick={() => {
                  setImpl(c.slug);
                  scrollToId("datanodes");
                }}
                className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1 text-left transition-colors ${
                  impl?.slug === c.slug ? "border-primary/40 bg-primary/10 text-white" : "border-white/10 bg-transparent text-gray-300 hover:border-white/30"
                }`}
              >
                <span className="font-semibold">{implLabel(c)}</span>
                <span className="ml-auto font-mono text-[11px] text-gray-500">x-chain: {c.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      </ProviderCard>

      {providers.nodegate && (
        <ProviderCard service={nodegate} accent={nodegate.accent} anchor="nodegate" external={{ href: nodegate.href, label: "nodegate.nodefleet.net" }}>
          <p className="m-0 mb-2 text-gray-400">{providers.nodegate.detail}</p>
          <div className="flex flex-wrap gap-1">
            {NODEGATE_STACK_SERVICES.map((s) => (
              <span key={s} className="rounded-md border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-gray-300">
                {s}
              </span>
            ))}
          </div>
        </ProviderCard>
      )}

      {providers.metrics && (
        <ProviderCard service={chaingraph} accent={chaingraph.accent} anchor="metrics" external={{ href: providers.metrics.href, label: "Dashboard" }}>
          <p className="m-0 mb-2 text-gray-400">
            Public dashboard for {providers.metrics.name} ({providers.metrics.detail}) — activity, wallets, fees, TPS — with audit and methodology endpoints.
          </p>
          <p className="m-0 break-all font-mono text-[11px] text-gray-300">{providers.metrics.api.dashboard.replace("https://", "")}</p>
        </ProviderCard>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Datanodes — one implementation (slug) of the network.
// ---------------------------------------------------------------------------
const ImplStrip = ({ chain, fam }) => (
  <div className="doc-reveal mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <ChainLogo chain={chain} size={28} />
    <span className="text-sm font-semibold text-white">{chain.name}</span>
    <span className="rounded-md px-2 py-0.5 text-[11px] font-semibold" style={{ color: fam.accent, backgroundColor: `${fam.accent}1A` }}>
      {fam.interface}
    </span>
    {chain.aliases?.length > 0 && <span className="text-xs text-gray-500">aliases: {chain.aliases.join(" · ")}</span>}
    <span className="ml-auto flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0A14] py-1 pl-3 pr-1 font-mono text-xs text-gray-300">
      x-chain: <span className="text-white">{chain.slug}</span>
      <CopyButton text={chain.slug} label="Copy slug" />
    </span>
  </div>
);

// Sui gets the full article-style guide (GraphQL + gRPC).
const SuiGuide = ({ chain, fam }) => (
  <>
    <ImplStrip chain={chain} fam={fam} />
    <Callout accent={fam.accent} title={SUI_META.title}>
      {SUI_META.excerpt}
    </Callout>
    {SUI_BLOCKS.map((block, i) => (
      <Block key={i} block={block} accent={fam.accent} />
    ))}
    <div className="doc-reveal mt-12 flex flex-wrap items-center gap-3 border-t border-white/10 pt-6 text-xs text-gray-500">
      <span>
        {SUI_META.author} · {SUI_META.date} · {SUI_META.readingTime}
      </span>
      <Link to="/articles/datanodes-sui-api-reference" className="ml-auto text-primary no-underline hover:underline">
        Article version →
      </Link>
    </div>
  </>
);

// Every other implementation is generated from its family template.
const GenericImpl = ({ chain, network, fam }) => {
  const examples = EXAMPLES[chain.family] || [];
  const methods = METHODS[chain.family] || [];
  const isRest = chain.family === "cosmos-rest";
  const grpc = chain.family === "solana" ? GRPC.find((g) => g.name.startsWith("Solana")) : null;

  const infoRows = [
    ["Interface", fam.interface],
    ["Transport", fam.transport],
    ["Network", network.fullName],
    ...(network.chainId !== undefined ? [["Chain id", String(network.chainId)]] : []),
    ["x-chain slug", chain.slug],
    ...(chain.aliases?.length ? [["Aliases", chain.aliases.join(" · ")]] : []),
    ["Updated", UPDATED],
  ];

  return (
    <>
      <ImplStrip chain={chain} fam={fam} />

      <SectionHeading id="overview" eyebrow={fam.short} title="Overview" n="02.1" accent={fam.accent}>
        {fam.blurb}
      </SectionHeading>
      <KVCard pill={fam.short} accent={fam.accent} rows={infoRows} className="mb-6" />
      {chain.family === "other" && (
        <Callout accent={fam.accent} tone="warn" title="Native method set">
          This network is proxied as-is. Use the method names from the chain's own RPC documentation; the gateway adds authentication,
          metering and routing but does not translate the protocol.
        </Callout>
      )}

      <SectionHeading id="endpoints" eyebrow={fam.short} title="Endpoints & headers" n="02.2" accent={fam.accent}>
        Use either form — they are equivalent. {isRest && "For REST chains the path after the key is forwarded upstream unchanged."}
      </SectionHeading>
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <KVCard
          pill="Header form"
          accent={fam.accent}
          rows={[
            ["Method", isRest ? "GET / POST" : "POST"],
            ["URL", isRest ? `${GATEWAY.rpc}/<rest path>` : `${GATEWAY.rpc}/`],
            ["x-api-key", "{{KEY}}"],
            ["x-chain", chain.slug],
            ...(isRest ? [] : [["Content-Type", "application/json"]]),
          ]}
        />
        <KVCard
          pill="Path form"
          accent={fam.accent}
          rows={[
            ["URL", `${GATEWAY.rpc}/${chain.slug}/{{KEY}}/${isRest ? "<rest path>" : ""}`],
            ["Headers", "none required"],
            ["Use when", "your client only accepts a URL (wallets, SDK providers, dashboards)"],
          ]}
        />
      </div>

      <SectionHeading id="examples" eyebrow={fam.short} title={`Calling ${chain.name}`} n="02.3" accent={fam.accent}>
        Copy, paste, run. Responses below were captured from live nodes; values such as heights and balances move with the chain.
      </SectionHeading>
      {examples.map((ex, i) => (
        <div key={i}>
          <h3 className="doc-reveal mb-2 mt-6 text-base font-bold text-white">{ex.title}</h3>
          <CodeBlock code={buildCurl({ slug: chain.slug, body: ex.body, path: ex.path, method: ex.method })} label="Request" response={ex.response} />
          {ex.note && <p className="doc-reveal -mt-3 mb-4 text-xs text-gray-500">{ex.note}</p>}
        </div>
      ))}

      <SectionHeading id="methods" eyebrow={fam.short} title="What's available" n="02.4" accent={fam.accent}>
        {chain.family === "evm"
          ? "Standard Ethereum JSON-RPC. Archive-depth reads and debug_/trace_ namespaces depend on the node behind the slug — ask us if you need them."
          : `The ${fam.short} method catalog served for ${chain.name}.`}
      </SectionHeading>
      {methods.map((g) => (
        <div key={g.group}>
          <h3 className="doc-reveal mb-2 mt-6 text-base font-bold text-white">{g.group}</h3>
          <DocTable headers={["Method", "Returns"]} rows={g.rows} accent={fam.accent} mono />
        </div>
      ))}

      {chain.ws && (
        <>
          <SectionHeading id="websocket" eyebrow={fam.short} title="WebSocket" n="02.5" accent={fam.accent}>
            {chain.name} supports subscriptions over the WebSocket gateway. Use <Mono>x-chain: {chain.ws}</Mono>.
          </SectionHeading>
          <KVCard pill="WSS" accent={fam.accent} rows={[["URL", WEBSOCKET.url], ["x-api-key", "{{KEY}}"], ["x-chain", chain.ws]]} className="mb-4" />
          <CodeBlock code={WEBSOCKET.example.replace('"eth-mainnet"', `"${chain.ws}"`)} label="Node.js — ws" />
        </>
      )}

      {grpc && (
        <>
          <SectionHeading id="grpc" eyebrow={fam.short} title="gRPC streams (Yellowstone)" n="02.5" accent={fam.accent}>
            {grpc.note}
          </SectionHeading>
          <KVCard pill="gRPC" accent={fam.accent} rows={[["Target", grpc.target], ["Transport", "TLS · HTTP/2"], ["x-api-key", "{{KEY}} (metadata)"], ["Service", grpc.services]]} className="mb-4" />
          <CodeBlock
            label="grpcurl — subscribe to slot updates (supply the yellowstone protos)"
            code={`grpcurl -H "x-api-key: {{KEY}}" \\
  -import-path ./proto -proto geyser.proto \\
  -d '{"slots": {"client": {}}}' \\
  ${grpc.target} geyser.Geyser/Subscribe`}
          />
        </>
      )}

      <SectionHeading id="limits" eyebrow={fam.short} title="Rate limits & errors" n={chain.ws || grpc ? "02.6" : "02.5"} accent={fam.accent}>
        Limits are enforced per organization (RPS + monthly volume). Gateway errors carry a <Mono>(ref: NF-XXNN)</Mono> code; node
        errors pass through unchanged.
      </SectionHeading>
      <DocTable headers={["Capability", "Free tier", "Paid tiers"]} rows={PLAN_ROWS} accent={fam.accent} />
      <DocTable headers={["HTTP status", "Meaning"]} rows={HTTP_STATUSES} accent={fam.accent} mono />
      <DocTable headers={["Ref", "Meaning"]} rows={NF_CODES} accent={fam.accent} mono />
    </>
  );
};

const DatanodesSection = ({ network, impl, setImpl }) => {
  const fam = FAMILIES[impl.family];
  const protocols = [...new Set(network.chains.map(implLabel))].join(", ");
  return (
    <>
      <SectionHeading id="datanodes" eyebrow="Datanodes" title="Datanodes API" n="02" accent={fam.accent}>
        {network.fullName} is served by Datanodes over {protocols}.{" "}
        {network.chains.length > 1 ? "Pick an implementation — endpoints, live examples and the method catalog are generated per slug." : "Endpoints, live examples and the method catalog below are generated for its slug."}
      </SectionHeading>

      {network.chains.length > 1 && (
        <div className="doc-reveal mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Datanodes implementation">
          {network.chains.map((c) => {
            const active = c.slug === impl.slug;
            const cf = FAMILIES[c.family];
            return (
              <button
                key={c.slug}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setImpl(c.slug)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active ? "text-white" : "border-white/10 bg-transparent text-gray-400 hover:border-white/30 hover:text-white"
                }`}
                style={active ? { borderColor: cf.accent, backgroundColor: `${cf.accent}1F` } : undefined}
              >
                {implLabel(c)}
                <span className="font-mono text-[10px] font-normal text-gray-500">{c.slug}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* key = slug so per-implementation state (tables, reveals) resets on switch */}
      <div key={impl.slug}>{impl.dedicated ? <SuiGuide chain={impl} fam={fam} /> : <GenericImpl chain={impl} network={network} fam={fam} />}</div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Nodegate + Chain Graph sections (only when the service covers the network).
// ---------------------------------------------------------------------------
const NodegateSection = ({ network, entry, n }) => {
  const svc = serviceById("nodegate");
  return (
    <>
      <SectionHeading id="nodegate" eyebrow="Nodegate" title="Nodegate stack" n={n} accent={svc.accent}>
        Nodegate runs {network.name} on the Nodefleet cluster: {entry.detail}. Stacks are deployed as kustomize overlays and ship the
        standard Nodegate service set.
      </SectionHeading>
      <KVCard
        pill="Nodegate"
        accent={svc.accent}
        rows={[
          ["Network", network.fullName],
          ["Deployment", entry.detail],
          ["Stack services", NODEGATE_STACK_SERVICES.join(" · ")],
          ["Control API", NODEGATE_API.replace("https://", "")],
        ]}
        className="mb-4"
      />
      <p className="doc-reveal m-0 text-sm text-gray-400">
        <a href={svc.href} target="_blank" rel="noopener noreferrer" className="font-semibold no-underline hover:underline" style={{ color: svc.accent }}>
          Open Nodegate ↗
        </a>{" "}
        to deploy your own stack or request access to this one.
      </p>
    </>
  );
};

const MetricsSection = ({ network, entry, n }) => {
  const svc = serviceById("chaingraph");
  return (
    <>
      <SectionHeading id="metrics" eyebrow="Nodefleet Metrics" title="Chain Graph analytics" n={n} accent={svc.accent}>
        Chain Graph publishes a public, auditable dashboard for {entry.name} ({entry.detail}) from Nodefleet-operated nodes. The same
        data is exposed by the Chain Graph API — no key required.
      </SectionHeading>
      <KVCard
        pill="Chain Graph"
        accent={svc.accent}
        rows={[
          ["Network", network.fullName],
          ["Dashboard", entry.href],
          ["API · dashboard", entry.api.dashboard],
          ["API · audit", entry.api.audit],
          ["API · methodology", entry.api.methodology],
        ]}
        className="mb-4"
      />
      <CodeBlock label="Dashboard data — JSON" code={`curl -s ${entry.api.dashboard}`} />
      <p className="doc-reveal m-0 text-sm text-gray-400">
        <a href={entry.href} target="_blank" rel="noopener noreferrer" className="font-semibold no-underline hover:underline" style={{ color: svc.accent }}>
          Open the {entry.name} dashboard ↗
        </a>
      </p>
    </>
  );
};

// ---------------------------------------------------------------------------
// Page body (route slug already canonical here).
// ---------------------------------------------------------------------------
const NetworkBody = ({ network }) => {
  const { impl, setImpl } = useNetworkRoute();
  const group = groupOfNetwork(network);
  const providers = providersForNetwork(network.slug);
  const fam = FAMILIES[network.family];
  const active = impl || network.chains[0];

  const siblings = group.networks.filter((n) => n.slug !== network.slug);
  const familyPeers = networksByFamily(network.family).filter((n) => n.groupId !== network.groupId);

  let n = 2;
  const nodegateN = providers.nodegate ? String(++n).padStart(2, "0") : null;
  const metricsN = providers.metrics ? String(++n).padStart(2, "0") : null;

  const sections = useMemo(
    () => [
      { id: "services", text: "Available on this network" },
      { id: "datanodes", text: "Datanodes API" },
      ...implSections(active).map((s) => ({ ...s, sub: true })),
      ...(providers.nodegate ? [{ id: "nodegate", text: "Nodegate stack" }] : []),
      ...(providers.metrics ? [{ id: "metrics", text: "Chain Graph analytics" }] : []),
    ],
    [active, providers.nodegate, providers.metrics],
  );

  return (
    <div className="flex gap-10" style={{ "--accent": fam.accent }}>
      <article className="min-w-0 max-w-4xl flex-1">
        <NetworkHeader network={network} group={group} providers={providers} fam={fam} />

        <SectionHeading id="services" eyebrow="API reference" title="Available on this network" n="01" accent={fam.accent}>
          Nodefleet services that cover {network.fullName}. Datanodes serves the RPC traffic
          {providers.nodegate ? ", Nodegate operates the chain stack" : ""}
          {providers.metrics ? ", Chain Graph publishes the metrics" : ""}.
        </SectionHeading>
        <ProvidersGrid network={network} providers={providers} impl={active} setImpl={setImpl} />

        <DatanodesSection network={network} impl={active} setImpl={setImpl} />

        {providers.nodegate && <NodegateSection network={network} entry={providers.nodegate} n={nodegateN} />}
        {providers.metrics && <MetricsSection network={network} entry={providers.metrics} n={metricsN} />}

        {(siblings.length > 0 || familyPeers.length > 0) && (
          <div className="doc-reveal mt-12 border-t border-white/10 pt-6">
            {siblings.length > 0 && (
              <>
                <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Other {group.name} networks</p>
                <div className="mb-6 flex flex-wrap gap-2">
                  {siblings.map((s) => (
                    <Link
                      key={s.slug}
                      to={networkPath(s.slug)}
                      className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] py-1 pl-1 pr-3 text-xs text-gray-300 no-underline transition-colors hover:border-primary/40 hover:text-white"
                    >
                      <ChainLogo chain={{ name: s.name, logo: s.logo, family: s.family }} size={20} />
                      {s.label}
                      {s.chainId !== undefined && <span className="font-mono text-[10px] text-gray-500">{String(s.chainId)}</span>}
                    </Link>
                  ))}
                </div>
              </>
            )}
            {familyPeers.length > 0 && (
              <>
                <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">More {fam.short} networks</p>
                <div className="flex flex-wrap gap-2">
                  {familyPeers.map((s) => (
                    <Link
                      key={s.slug}
                      to={networkPath(s.slug)}
                      className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] py-1 pl-1 pr-3 text-xs text-gray-300 no-underline transition-colors hover:border-primary/40 hover:text-white"
                    >
                      <ChainLogo chain={{ name: s.name, logo: s.logo, family: s.family }} size={20} />
                      {s.fullName}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </article>
      <OnThisPage sections={sections} accent={fam.accent} />
    </div>
  );
};

const NetworkPage = () => {
  const { slug } = useParams();
  const resolved = getNetwork(slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!resolved) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center">
        <h1 className="mb-2 text-3xl font-bold text-white">Network not found</h1>
        <p className="mb-6 text-gray-400">
          <Mono>{slug}</Mono> is not a network of the reference. Browse the catalog or ask us to add it.
        </p>
        <Link to={NETWORKS_PATH} className="rounded-full bg-primary px-5 py-2 font-semibold text-space-deep no-underline hover:bg-primary-400">
          Back to the reference
        </Link>
      </div>
    );
  }

  // Implementation slugs and aliases redirect to the network's canonical URL,
  // keeping the implementation they pointed at selected (?impl=).
  const { network, chain } = resolved;
  if (network.slug !== slug) {
    const implSlug = chain && chain.slug !== network.chains[0].slug ? chain.slug : null;
    return <Navigate to={networkPath(network.slug, implSlug)} replace />;
  }

  return <NetworkBody network={network} />;
};

export default NetworkPage;
